import {
  UserInfo,
  ConversationRequest,
  Conversation,
  ChatMessage,
  CosmosDBHealth,
  CosmosDBStatus,
  UserLoginDetails,
  ShareModal,
} from "./models";
import { ReportinghubDomain } from "../constants/constants";
const localKey = "c1c1b5c32a07359d1f3c7aa06e9c22e7";
function getHeaders(isFile = false) {
  const headers: { [key: string]: string } = {};
  if (!isFile) {
    headers["Content-Type"] = "application/json";
  }

  if (sessionStorage.getItem("isReportingHub") === "true") {
    headers["userEmail"] = sessionStorage.getItem("RHUserEmail") ?? "";
    headers["userName"] = sessionStorage.getItem("RHUserName") ?? "";
  } else if (sessionStorage.getItem("msalUserEmail")) {
    headers["userEmail"] = sessionStorage.getItem("msalUserEmail") ?? "";
    headers["userName"] = sessionStorage.getItem("msalUserName") ?? "";
  } else {
    headers["userEmail"] = "Test User";
    headers["userName"] = "testusername@constoso.com";
  }
  return headers;
}

export async function generateToken(name?, email?) {
  const key =
    window.location.hostname === "127.0.0.1"
      ? btoa(localKey)
      : btoa("8167fdec35cd7284b8d056007c1aefe8");
  const header =
    window.location.hostname === "127.0.0.1"
      ? {
          userName: "Test User",
          userEmail: "testusername@constoso.com",
          "Content-Type": "application/json",
        }
      : name != undefined || email != undefined
      ? {
          userName: name,
          userEmail: email,
          "Content-Type": "application/json",
        }
      : getHeaders();
  try {
    const response = await fetch("/token_generator", {
      method: "POST",
      headers: header,
      body: JSON.stringify({
        key: key,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to generate token");
    }

    const data = await response.json();
    sessionStorage.setItem("Token", data.token);
    return true;
  } catch (error) {
    console.error("Error in token generation:", error);
    return false;
  }
}

async function apiFetch(url, option: RequestInit = {}) {
  // Check if token exists and is valid
  if (
    sessionStorage.getItem("Token") === null ||
    sessionStorage.getItem("Token") === undefined ||
    sessionStorage.getItem("Token") === "undefined" ||
    sessionStorage.getItem("Token").length === 0
  ) {
    const tokenGenerated = await generateToken();
    if (!tokenGenerated) {
      throw new Error("Failed to generate token");
    }
  }

  // Set Authorization header with token
  const options = {
    ...option, // Merge any existing options
    headers: {
      ...(option.headers || {}), // Merge existing headers if any
      Token: `${sessionStorage.getItem("Token")}`, // Set the Authorization header with the token
    },
  };

  try {
    const response = await fetch(url, options); // Use the options with updated headers

    if (!response.ok && (response.status === 401 || response.status === 403)) {
      // If the token was invalid, retry once after generating a new token
      const tokenGenerated = await generateToken();
      if (!tokenGenerated) {
        throw new Error("Failed to generate token on retry");
      }
      // Retry with the new token
      options.headers["Token"] = `${sessionStorage.getItem("Token")}`;
      return await fetch(url, options); // Use the updated token
    }
    return response;
  } catch (error) {
    console.error("API Fetch error:", error);
    throw error;
  }
}

// to be called on load
export async function getUserInfo(): Promise<UserInfo[]> {
  const response = await apiFetch("/.auth/me");
  if (!response.ok) {
    console.log("No identity provider found. Access to chat will be blocked.");
    return [];
  }

  const payload = await response.json();
  return payload;
}
export const historyEnsure = async (): Promise<CosmosDBHealth> => {
  const response = await apiFetch("/history/ensure", {
    method: "GET",
    headers: getHeaders(),
  })
    .then(async (res) => {
      let respJson = await res.json();
      let formattedResponse;
      if (respJson.message) {
        formattedResponse = CosmosDBStatus.Working;
      } else {
        if (res.status === 500) {
          formattedResponse = CosmosDBStatus.NotWorking;
        } else {
          formattedResponse = CosmosDBStatus.NotConfigured;
        }
      }
      if (!res.ok) {
        return {
          cosmosDB: false,
          status: formattedResponse,
        };
      } else {
        return {
          cosmosDB: true,
          status: formattedResponse,
        };
      }
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      return {
        cosmosDB: false,
        status: err,
      };
    });
  return response;
};

export const frontendSettings = async (): Promise<Response | null> => {
  const response = await fetch("/frontend_settings", {
    method: "GET",
    headers: getHeaders(),
  })
    .then((res) => {
      return res.json();
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      return null;
    });

  return response;
};

export const historyList = async (
  offset = 0
): Promise<Conversation[] | null> => {
  const response = await apiFetch(`/history/list?offset=${offset}`, {
    method: "GET",
    headers: getHeaders(),
  })
    .then(async (res) => {
      const payload = await res.json();
      if (!Array.isArray(payload)) {
        console.error("There was an issue fetching your data.");
        return null;
      }
      const conversations: Conversation[] = await Promise.all(
        payload.map(async (conv: any) => {
          let convMessages: ChatMessage[] = [];
          convMessages = await historyRead(conv.id)
            .then((res) => {
              return res;
            })
            .catch((err) => {
              console.error("error fetching messages: ", err);
              return [];
            });
          const conversation: Conversation = {
            id: conv.id,
            title: conv.title,
            date: conv.updatedAt,
            messages: convMessages,
            summary: conv.summary,
            pinned:conv.pinned
          };
          return conversation;
        })
      );
      return conversations;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      return null;
    });

  return response;
};

export const historyRead = async (convId: string): Promise<ChatMessage[]> => {
  const response = await apiFetch("/history/read", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: getHeaders(),
  })
    .then(async (res) => {
      if (!res) {
        return [];
      }
      const payload = await res.json();
      let messages: ChatMessage[] = [];
      if (payload?.messages) {
        payload.messages.forEach((msg: any) => {
          const message: ChatMessage = {
            id: msg.id,
            role: msg.role,
            date: msg.createdAt,
            content: msg.content,
            feedback: msg.feedback ?? undefined,
            language: msg.language ?? "",
            isEdited: msg.isEdited ?? false,
            isRegenerated: msg.isRegenerated ?? false,
          };
          messages.push(message);
        });
      }
      return messages;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      return [];
    });
  return response;
};

export const historyGenerate = async (
  options: ConversationRequest,
  abortSignal: AbortSignal,web?,
  cancel?: Boolean,
  convId?: string
): Promise<Response> => {
  let body;
  if (convId) {
    body = cancel
      ? JSON.stringify({
          conversation_id: convId,
          messages: options.messages,
        cancel_flag: cancel ? "True" : "false",
          web:web
        })
      : JSON.stringify({
          conversation_id: convId,
          messages: options.messages, web:web
        });
  } else {
    body = cancel
      ? JSON.stringify({
          messages: options.messages,
          cancel_flag: cancel ? "True" : "false", web:web
        })
      : JSON.stringify({
          messages: options.messages, web:web
        });
  }
  const response = await apiFetch("/history/generate", {
    method: "POST",
    headers: getHeaders(),
    body: body,
    signal: abortSignal,
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");

      if (err.name === "AbortError") {
        let errRes: Response = {
          ...new Response(),
          ok: false,
          status: 200,
        };
        console.log("Fetch aborted");
        return errRes;
      }
      return new Response();
    });

  return response;
};

export const historyUpdate = async (
  messages: ChatMessage[],
  convId: string
): Promise<Response> => {
  const response = await apiFetch("/history/update", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      messages: messages,
    }),
    headers: getHeaders(),
  })
    .then(async (res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDelete = async (convId: string): Promise<Response> => {
  const response = await apiFetch("/history/delete", {
    method: "DELETE",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: getHeaders(),
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyDeleteAll = async (): Promise<Response> => {
  const response = await apiFetch("/history/delete_all", {
    method: "DELETE",
    body: JSON.stringify({}),
    headers: getHeaders(),
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyClear = async (convId: string): Promise<Response> => {
  const response = await apiFetch("/history/clear", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
    }),
    headers: getHeaders(),
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyRename = async (
  convId: string,
  title: string
): Promise<Response> => {
  const response = await apiFetch("/history/rename", {
    method: "POST",
    body: JSON.stringify({
      conversation_id: convId,
      title: title,
    }),
    headers: getHeaders(),
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue fetching your data.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

export const historyMessageFeedback = async (
  messageId: string,
  feedback: string
): Promise<Response> => {
  const response = await apiFetch("/history/message_feedback", {
    method: "POST",
    body: JSON.stringify({
      message_id: messageId,
      message_feedback: feedback,
    }),
    headers: getHeaders(),
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.error("There was an issue logging feedback.");
      let errRes: Response = {
        ...new Response(),
        ok: false,
        status: 500,
      };
      return errRes;
    });
  return response;
};

// import { chatHistorySampleData } from "../constants/chatHistory";

export async function conversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal
): Promise<Response> {
  const response = await apiFetch("/conversation", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      messages: options.messages,
    }),
    signal: abortSignal,
  });

  return response;
}
// export const apiFetchChatHistoryInit = async (): Promise<Conversation[] | null> => {
// export const apiFetchChatHistoryInit = (): Conversation[] | null => {
//     // Make initial API call here

//     // return null;
//     return chatHistorySampleData;
// }

/**
 * Retrieves the user name from the Reporting Hub.
 * @returns A promise that resolves to an array containing the user name.
 */
export async function getRHUserName(): Promise<string> {
  const response = await fetch(ReportinghubDomain + "/Profile/GetName", {
    credentials: "include",
    cache: "reload",
  });
  if (!response.ok) {
    console.log("Failed to get user name from RH");
    return "";
  }

  const payload = await response.text();
  return payload;
}

/**
 * Retrieves the Reporting Hub authentication URL.
 * @returns A promise that resolves to the authentication URL.
 */
export async function getRHAuthUrl(data): Promise<string> {
  const response = await fetch("/getReportingHubAuthUrl", {
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    console.log("Failed to get auth url from RH");
    return "";
  }

  const payload = await response.json();
  return payload;
}

/**
 * Retrieves the email of the reporting hub user.
 * @param code - The code used to retrieve the email.
 * @returns A promise that resolves to the user's email.
 */
export async function getReportingHubUserEmail(code: string): Promise<string> {
  const response = await fetch("/getReportingHubUserEmail", {
    method: "POST",
    body: JSON.stringify({
      code: code,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    console.log("Failed to get user email from RH");
    return "";
  }

  const payload = await response.json();
  return payload;
}

export async function logUserLoginDetails(
  userLoginDetails: UserLoginDetails
): Promise<Response> {
  await generateToken(userLoginDetails.user_name, userLoginDetails.user_email);
  const header = getHeaders();
  header["Token"] = sessionStorage.getItem("Token");
  const response = await fetch("/loguserlogindetails", {
    method: "POST",
    body: JSON.stringify({
      userLoginDetails: userLoginDetails,
    }),
    headers: header,
  });
  if (!response.ok) {
    console.log("Failed to log user login details");
  }

  return response;
}

export async function getPreDefinedQuestions(): Promise<Response> {
  const response = await apiFetch("/cards/random", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    console.log("Failed to get pre-defined questions");
  }

  return response.json();
}
export async function getUploadedDocument(): Promise<Response> {
  const response = await apiFetch("/document_upload_history", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}
export async function getUploadAcess(): Promise<Response> {
  const response = await apiFetch("/accessUpload", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}
export async function selfUploadApi(data: FormData): Promise<Response> {
  const response = await apiFetch("/upload_documents", {
    method: "POST",
    body: data,
    headers: getHeaders(true),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function shareModalAPI(data: ShareModal): Promise<Response> {
  const response = await apiFetch("/share_conversation", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getallcompanies(): Promise<Response> {
  const response = await apiFetch("/dropdown/companyname", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getallindustries(): Promise<Response> {
  const response = await apiFetch("/dropdown/sector", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getalllocations(): Promise<Response> {
  const response = await apiFetch("/dropdown/location", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getShareData(data): Promise<Response> {
  const response = await fetch("/share", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getUserProfileDetails(): Promise<Response> {
  const response = await apiFetch("/fetch_userprofile", {
    method: "POST",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function updateUserProfileDetails(data): Promise<Response> {
  const response = await apiFetch("/upsert_userprofile", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: response.status,
    };
    return errRes;
  }

  return response;
}

export async function getSharedChat(id): Promise<Response> {
  const response = await apiFetch("/share/login", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ conversation_id: id }),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getRoles(): Promise<Response> {
  const response = await apiFetch("/dropdown/roles", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function getDocumentDetails(): Promise<Response> {
  const response = await apiFetch("/documents_to_review", {
    method: "GET",
    headers: getHeaders(),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}
export async function actionApi(payload): Promise<Response> {
  const response = await apiFetch("/review_action", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}
export async function FetchExternalTransactionDocuments(payload): Promise<Response> {
  const response = await apiFetch("/transaction_document", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500
    };
    return errRes;
  }

  return response;
}
export async function DownloadFile(payload): Promise<Response> {
  const response = await apiFetch("/DownloadFile", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}
export async function companyList(payload): Promise<Response> {
  const response = await apiFetch("/companies_list", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}

export async function pinUnpinConversation(payload): Promise<Response> {
  const response = await apiFetch("/pin_conversation", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let errRes: Response = {
      ...new Response(),
      ok: false,
      status: 500,
    };
    return errRes;
  }

  return response;
}